import React from 'react';
import { FC } from 'react';
import styled from '@emotion/styled';
import {
  chakra,
  ChakraProps,
  Tooltip,
  useStyleConfig,
  useTheme,
  Icon as ChakraIcon,
  IconProps,
} from '@chakra-ui/react';
import { Button } from '../Button/Button';
import { Flex } from '../Flex/Flex';
import { UseSizeOptions, useSizes } from '../utils/useSizes';
import { omitComponentProps } from '../utils/omitComponentProps';
import { primaryProps } from '../utils/primaryProps';

const useIcon = (icon: any) => {
  const theme = useTheme();
  if (!icon) {
    return theme.icons.IcAlert;
  }
  if (typeof icon === 'string') {
    return theme.icons[icon];
  }
  return icon;
};

export const IconWrap = ({
  icon,
  onClick,
  tooltip,
  tooltipProps,
  wrapProps,
  buttonProps,
  buttonComponent,
  ...props
}: {
  icon: string | React.JSX.Element;
  onClick?: () => void;
  tooltip?: string;
  tooltipProps?: ChakraProps;
  wrapProps?: ChakraProps;
  buttonProps?: ChakraProps;
  buttonComponent?: React.ElementType;
  variant?: string;
  size?: UseSizeOptions;
} & IconProps &
  ChakraProps) => {
  const realIcon = useIcon(icon);
  const { getSize } = useSizes(props.size || 'md');
  const Wrap = tooltip ? Tooltip : Flex;
  const asButtonComponent = buttonComponent ? buttonComponent : undefined;
  const styles: any = useStyleConfig('Icon', {
    variant: props.variant || 'default',
    size: getSize() || 'md',
  });

  if (onClick) {
    return (
      <Wrap
        label={tooltip}
        placement="top"
        borderRadius={6}
        {...tooltipProps}
        {...wrapProps}
        alignItems="center"
      >
        <Button
          as={asButtonComponent}
          variant="transparent"
          onClick={onClick}
          {...buttonProps}
          cursor="pointer"
          px={0}
          py={0}
          w={styles.icon?.boxSize || 'full'}
          h={styles.icon?.boxSize || 'full'}
          css={{
            '& > span': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        >
          <ChakraIcon as={realIcon} {...props} {...(styles?.icon || {})} />
        </Button>
      </Wrap>
    );
  }

  return (
    <Wrap
      label={tooltip}
      placement="top"
      borderRadius={6}
      {...tooltipProps}
      {...wrapProps}
    >
      <Flex
        alignItems="center"
        justifyContent="center"
        w={styles.icon?.boxSize || 'full'}
        h={styles.icon?.boxSize || 'full'}
      >
        <ChakraIcon as={realIcon} {...props} {...(styles?.icon || {})} />
      </Flex>
    </Wrap>
  );
};

export const Icon: FC<
  ChakraProps & {
    [x: string]: any;
  }
> = styled(omitComponentProps(chakra(IconWrap), ['primary']))`
  ${primaryProps};
`;
